import type { CaptionCard, CaptionStyle, Word } from "@hudhud/core";
import { COLORS, HEIGHT, WIDTH } from "../types.js";
import { clampMap, interpolate } from "./interpolate.js";
import { spring } from "./spring.js";

/** A card stays visible until the next card starts (bridges tiny gaps), at most this long after its last word. */
export const CARD_HOLD_MS = 1200;

export function activeCard(cards: CaptionCard[], ms: number): { card: CaptionCard; index: number } | null {
  for (let i = 0; i < cards.length; i++) {
    const c = cards[i];
    const next = cards[i + 1];
    const holdUntil = next ? Math.min(next.startMs, c.endMs + CARD_HOLD_MS) : c.endMs + CARD_HOLD_MS;
    if (ms >= c.startMs && ms < holdUntil) return { card: c, index: i };
  }
  return null;
}

export function activeWordIndex(card: CaptionCard, ms: number): number {
  let idx = -1;
  card.words.forEach((w, i) => { if (ms >= w.startMs) idx = i; });
  return idx;
}

export function rgba(hex: string, alpha: number): string {
  const m = hex.replace("#", "");
  const n = parseInt(m.length === 3 ? m.split("").map((c) => c + c).join("") : m, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/** Dark text on light highlight pills, light text on dark ones. */
export function contrastOn(hex: string): string {
  const m = hex.replace("#", "");
  const n = parseInt(m.length === 3 ? m.split("").map((c) => c + c).join("") : m, 16);
  const lum = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
  return lum > 0.6 ? COLORS.ink : "#FFFFFF";
}

/** Where the caption block sits: centred at yPos, maxWidth wide, nudged by xOffset. */
export function captionBlock(style: CaptionStyle): { left: number; width: number; top: number } {
  const width = WIDTH * style.maxWidth;
  return { left: (WIDTH - width) / 2 + style.xOffset, width, top: HEIGHT * style.yPos };
}

/** How far in a card is, `local` frames after it started: 1 at rest; the pop and slide entrances spring in, the fade fades. */
export function cardEnter(style: CaptionStyle, local: number, fps: number): number {
  if (style.animation === "none") return 1;
  if (style.animation === "fade") return clampMap(local, [0, 6], [0, 1]);
  return spring({ frame: local, fps, config: { damping: 16, stiffness: 180, mass: 0.7 } });
}
/** The card's vertical offset for its entrance (px, before the -50 % centring). */
export function cardLift(style: CaptionStyle, enter: number): number {
  if (style.animation === "pop") return interpolate(enter, [0, 1], [24, 0]);
  if (style.animation === "slide") return interpolate(enter, [0, 1], [70, 0]);
  return 0;
}

export const cardStartFrame = (card: CaptionCard, fps: number) => Math.round((card.startMs / 1000) * fps);

/** The pop of the word being spoken: a spring from the word's own start. */
export const wordPop = (frame: number, word: Word, fps: number) => spring({ frame: frame - Math.round((word.startMs / 1000) * fps), fps, config: { damping: 12, stiffness: 260, mass: 0.5 } });

export interface WordLook { scale: number; color: string; opacity: number; underline: boolean; glow: boolean; pill: boolean; emphasis: boolean }
/** How one word of a card looks at `frame`: its scale, colour, opacity and marks, from the style's highlight and emphasis rules. */
export function wordLook(style: CaptionStyle, word: Word, index: number, active: number, frame: number, fps: number): WordLook {
  const isActive = index === active;
  const isPast = index < active;
  const pop = isActive && style.animation === "pop" ? wordPop(frame, word, fps) : 0;
  const emph = !!word.emphasis;
  let scale = 1 + (style.animation === "pop" && isActive && style.highlightMode !== "none" ? 0.1 * pop : 0);
  if (emph && style.emphasisStyle === "bigger") scale *= 1.12;
  let color = style.color;
  if (emph && style.emphasisStyle === "color") color = style.highlight;
  if (isActive && style.highlightMode === "color") color = style.highlight;
  if (isActive && style.highlightMode === "pill") color = contrastOn(style.highlight);
  const underline = (isActive && style.highlightMode === "underline") || (emph && style.emphasisStyle === "underline");
  const glow = isActive && style.highlightMode === "glow";
  const pill = isActive && style.highlightMode === "pill";
  return { scale, color: glow ? "#FFFFFF" : color, opacity: isPast || isActive || emph ? 1 : 0.78, underline, glow, pill, emphasis: emph };
}

/** The word as shown: upper-cased in the language's own rules when the style asks for it. */
export const upperText = (text: string, lang: string, upper: boolean) => (upper ? text.toLocaleUpperCase(lang === "tr" ? "tr-TR" : lang === "en" ? "en-US" : lang) : text);

/** Padding and corner radius of the caption box for each box style (px). */
export function captionBox(style: CaptionStyle): { padX: number; padY: number; radius: number } | null {
  if (style.box === "none") return null;
  if (style.box === "pill") return { padX: 34, padY: 14, radius: 999 };
  if (style.box === "glass") return { padX: 32, padY: 20, radius: 26 };
  return { padX: 28, padY: 18, radius: 20 };
}
